import ScrollableContent from "../../components/ScrollableContent";
import WhatToBring from "./what-to-bring";
import CoplayerCard from "./coplayer-card";
import SpotsLeft from "./spots-left";
import WhatToExpect from "./what-to-expect";
import BookNowFooterV2 from "./book-now-footer-v2";
import {ReactComponent as BackButton} from '../../images/utils/back-button.svg';
import {ReactComponent as ShareButton} from '../../images/utils/share-button.svg';
import { navigate, RouteComponentProps } from "@reach/router";
import { IBatch, IGymDetails } from "../../types/gyms";
import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { getActivityById, getGymById, getCoplayers } from "../../apis/gym/activities";
import { errorToast } from "../../components/Toast";
import BookNowFooter from "./book-now-footer";
import { EBookNowComingFromPage } from "../../types/checkout";
import { ECheckoutType } from "../../types/checkout";
import AboutTheActivity from "./about-the-activity";
import { convert24HourTo12Hour } from "../../utils/functions/utils";
import { formatDate, formatTimeIntToAmPm } from "../../utils/date";
import Circle from "../../components/circle";
import Loader from "../../components/Loader";
import {ReactComponent as LocationIcon} from '../../images/utils/location-icon.svg';
import { message } from "antd";
import MoreInfo from "./more-info";

interface IClassCheckout extends RouteComponentProps {
}

interface IPlayer {
    name: string;
    level: string;
    noOfBookings: number;
    gamesPlayed: number;
}

const BatchCheckoutV2: React.FC<IClassCheckout> = () => {

    const [loading, setLoading] = useState(true);
    const [batchDetails, setBatchDetails] = useState<IBatch>();
    const [gym, setGym] = useState<IGymDetails | null>(null);
    const [gotBatchDetails, setBatchDetailsCheck] = useState<Boolean>(false);
    const [gotGymDetails, setGymDetailsCheck] = useState<Boolean>(false);
    const [spotsLeft, setSpotsLeft] = useState<number>(0);
    const [spotsTotal, setSpotsTotal] = useState<number>(0);
    const [players, setPlayers] = useState<IPlayer[]>([]);
    const [gotCoplayers, setGotCoplayers] = useState<boolean>(false); 
    const [isFromApp, setIsFromApp] = useState(false);
    const [pastAppBookings, setPastAppBookings] = useState({});
    const { COPLAYER_CARD_ENABLED } = require("../../constants/activities");
    const [isShareButtonClicked, setIsShareButtonClicked] = useState(false);
    const [alreadyBookedRatedGame, setAlreadyBookedRatedGame] = useState(false);  // To check if the user has already booked a rated game  // To check if the players have been fetched

    const batchId = window.location.pathname.split("/")[3];

    useEffect(() => {
      // Get isFromApp from window object
      const userSource = window?.platformInfo?.platform || "web";
      const appFlag = userSource !== "web";
      setIsFromApp(appFlag);
  
      // Get pastAppBookings from window object or initialize empty
      const storedBookings = window?.pastAppBookings || {};
      setPastAppBookings(storedBookings);
    }, []);
  
  const { mutate: _getActivityById } = useMutation({
      mutationFn: getActivityById,
      onSuccess: (result) => {
        setBatchDetails(result.batch);
      },
      onError: (error) => {
        errorToast("Error in getting gym data");
      },
      onSettled: () => {
        setLoading(false);
      },
    });

    const { mutate: _getGymById } = useMutation({
      mutationFn: getGymById,
      onSuccess: (result) => {
          setGym(result.gym);
          setGymDetailsCheck(true);
      },
      onError: (error) => {
          errorToast("Error in getting gym data");
      },
    });

    const { mutate: _getCoplayers } = useMutation({
        mutationFn: getCoplayers,
        onSuccess: (result) => {  
            setPlayers(result.map((player: any) => ({name: player.name,
                        userId: player.userId, 
                        level: player.skillLevel, 
                        rating: player.rating,
                        noOfBookings: player.noOfGuests, 
                        gamesPlayed: player.activityBookCount})));
            const userId = window.localStorage["zenfitx-user-details"]
                                  ? JSON.parse(window.localStorage["zenfitx-user-details"]).id || "0"
                                  : "0";
            if(result.some((player: any) => player.userId === userId && batchDetails?.isRated)) {
                setAlreadyBookedRatedGame(true);
            } 
        },
        onError: (error) => {
            errorToast("Error in getting coplayers");
        },
        onSettled: () => {
            setGotCoplayers(true);
        },
    });
    
    const isCoplayerCardEnabled = COPLAYER_CARD_ENABLED.includes(batchDetails?.activity?.toUpperCase() || "");

    useEffect(() => {
      const userId = window.localStorage["zenfitx-user-details"]
                                  ? JSON.parse(window.localStorage["zenfitx-user-details"]).id || "0"
                                  : "0";
        _getActivityById({id: batchId.toString(), userId: userId.toString()});
        _getCoplayers(batchId);
        // setPlayers([{name: "Pratik", level: "Beginner", noOfBookings: 1, gamesPlayed: 0}, {name: "Nikita", level: "Amateur", noOfBookings: 2, gamesPlayed: 5}, {name: "Whiskey", level: "Intermediate", noOfBookings: 3, gamesPlayed: 23}]);
    }, []);

    useEffect(() => {
        if (batchDetails?.gymId) {
          _getGymById(String(batchDetails.gymId));
          setBatchDetailsCheck(true);
        }
    }, [batchDetails]);
    
    useEffect(() => {
        setSpotsLeft(batchDetails?.slots ? batchDetails?.slots - batchDetails?.slotsBooked : 0);
        setSpotsTotal(batchDetails?.slots ? batchDetails?.slots : 0);
    }, [batchDetails]);

    const shareUrl = window.location.href;

    const handleShare = () => {
      navigator.clipboard.writeText(shareUrl).then(() => {
        message.success("Link copied to clipboard! You can now paste it to share.");
      }).catch(err => {
        console.error('Failed to copy: ', err);
        message.error("Failed to copy link. Please try again.");
      });
    };

    const handleBookNowClick = () => {
      if(alreadyBookedRatedGame) {
        message.error("You have already joined this game!");
        return false;
      }
      return true;
    };

    useEffect(() => {
      const shareButton = document.getElementById("share-button");
      shareButton?.addEventListener("click", () => {
        if (gotGymDetails && gotBatchDetails) {
          // Check if running in React Native WebView on Android
          if (window.ReactNativeWebView && window.platformInfo?.platform === "android") {
            // Send message to React Native to open native share modal
            window?.ReactNativeWebView?.postMessage(JSON.stringify({
              type: 'shareContent',
              title: "ZenfitX",
              text: `Hey, Join me for ${batchDetails?.activityName} at ${("0" + batchDetails?.startTime.toString()).slice(-4).substring(0, 2)}:00 on ${new Date(`${batchDetails?.date}`).toDateString()} at the ${gym?.name}. Let's sweat it out together! 😬`,
              url: window.location.href,
            }));
          } else if (navigator.share) {
            // Use Web Share API for browsers that support it (including iOS WebView)
            navigator
              .share({
                title: "ZenfitX",
                text: `Hey, Join me for ${batchDetails?.activityName} at ${("0" + batchDetails?.startTime.toString()).slice(-4).substring(0, 2)}:00 on ${new Date(`${batchDetails?.date}`).toDateString()} at the ${gym?.name}. Let's sweat it out together! 😬`,
                url: window.location.href,
              })
              .then(() => console.log("Successful share"))
              .catch((error) => console.log("Error sharing", error));
          } else {
            console.log("Share not supported on this browser, do it the old way.");
          }
        }
      });
      shareButton?.removeEventListener("click", () => {
        setIsShareButtonClicked(false);
      });
    }, [isShareButtonClicked, gotBatchDetails, gotGymDetails]);

    const gymId = batchDetails?.gymId;
    const activityName = batchDetails?.activityName ? batchDetails?.activityName : "";
    const activity = batchDetails?.activity ? batchDetails?.activity : "";
    const location = gym?.name ? gym?.name + (gym?.area ? ", " + gym.area : "") : "";

    const navigateToHome = () => {
        navigate(`/gym/${gymId}/batch`);
    }


  if(loading) {
      return <Loader />
  }

  return (
    <ScrollableContent bottomPadding={100}>
    <div className="batch-checkout-container flex flex-col">
        <div className="batch-hero-section flex flex-col bg-gradient-to-r from-black to-transparent bg-cover bg-center"
             style={{ backgroundImage: `linear-gradient(to right, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0)), url(${batchDetails?.image || require('../../images/utils/pickleball.png')})` }}>
          <div className="batch-hero-wrapper">
            <div className="flex flex-row justify-between pl-2 pt-2 pr-2">
              <BackButton onClick={() => navigateToHome()} />
              <ShareButton id="share-button" onClick={() => setIsShareButtonClicked(true)} />
            </div>
            <div className="flex flex-col px-6 text-white batch-hero-content">
              <p className="text-sm font-normal font-jakarta pt-3 batch-activity-type">{activity.toLowerCase()}</p>
              <p className="text-xl font-normal font-jakarta pt-1 batch-activity-name">{activityName}</p>
              <p className="text-xs font-normal font-jakarta pt-1 inline-flex items-center gap-1 batch-location"><LocationIcon />{location}</p>
              <div className="flex flex-row pt-1 pb-4 text-white font-jakarta text-2xl pt-3 batch-timing-info">
                {!gym?.isOnlyWeekend && <p className="text-white">{batchDetails?.date ? formatDate(batchDetails.date)["date suffix"] : "Date not available"}</p>}
                {!gym?.isOnlyWeekend && <p className="dotWhite"></p>}
                {batchDetails?.isDayPass ? <p className="text-white">All Day</p> : 
                <>
                  <p className="text-white">{formatTimeIntToAmPm(batchDetails?.startTime || 0)}</p>
                  <p className="dotWhite"></p>
                  <p className="text-white">{batchDetails?.DurationMin} mins</p>
                </>
                }
              </div>
            </div>
          </div>
        </div>
        <div className="batch-content-wrapper">
          <div className="batch-content-inner">
            {isCoplayerCardEnabled && <SpotsLeft spotsLeft={spotsLeft} spotsTotal={spotsTotal} />}
            {isCoplayerCardEnabled && <CoplayerCard players={players} loading={!gotCoplayers} spotsLeft={spotsLeft} spotsTotal={spotsTotal}/>}
            {batchDetails?.aboutTheActivity && <AboutTheActivity aboutTheActivity={batchDetails?.aboutTheActivity} />}
            {batchDetails?.whatToExpect && <WhatToExpect whatToExpect={batchDetails?.whatToExpect} />}
            {batchDetails?.whatToBring && <WhatToBring whatToBring={batchDetails?.whatToBring} />}
            {batchDetails?.moreInfo && <MoreInfo moreInfo={batchDetails?.moreInfo} />}
          </div>
        </div>
        {gym && (
          <BookNowFooter
            checkoutType={ECheckoutType.BATCH}
            batchDetails={batchDetails}
            gymData={gym}
            batchId={Number(batchId)}
            isFromApp={isFromApp}
            totalAmount={batchDetails?.price as number}
            comingFrom={EBookNowComingFromPage.BATCH_CHECKOUT_PAGE}
            forceBookNowCta={true}
            onBeforeAction={handleBookNowClick}
          />
        )}
      </div>
      </ScrollableContent>
  )
  
};

export default BatchCheckoutV2; 

